import './tab-navigation.scss';

import AjaxLoad from '../../utilities/js/ajax-load';
import { createElementFromHTML, checkAndLoadFiles, smoothScrollTo, getOffset } from '../../utilities/js/helper';
import Swiper from 'swiper';
import { Navigation, Mousewheel, Keyboard, A11y } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';
import 'swiper/scss/mousewheel';

class TabNavigation {
    constructor (element) {
        this.$tabNav = element;
        this.$tabNavItems = element.querySelectorAll('[data-tab-nav="item"]');
        this.$sliderContainer = element.querySelector('[data-tab-nav="slider"]');
        this.$targetWrapper = element.nextElementSibling;
        this.$target = this.$targetWrapper.querySelector('[data-tab-nav="target"]');
        this.$activeTab = this.$sliderContainer.querySelector('[data-tab-nav="item"].is--active');
        this.ajaxActive = false;
        this.tabNavName = this.$tabNav.dataset.tabNavName;
        this.loader = null;
        this.store = [];
        this.$tabMainDocument = element.querySelector('[data-tab-nav-main]');
        this.$elementToHide = document.querySelector('[data-tab-nav="hide"]');
        this.slider = null;
        this.onBoardSubpage = null;
        this.waitTimeout = null;
        this.$header = document.querySelector('[data-header="root"]');
    }

    initialize () {
        const currentParams = new URLSearchParams(window.location.search);
        const activeTabParam = currentParams.get('activeTab');

        if (this.$tabMainDocument) {
            this.store[0] = this.$target.children;
        }

        if (typeof onBoardSubpage !== 'undefined') {
            this.onBoardSubpage = this.$tabNav.dataset.tabNavType ? onBoardSubpage : null; // eslint-disable-line
        }

        if (!this.$target) {
            this.$target = document.querySelector('[data-tab-nav="target"]');
        }

        this.$tabNav.classList.add('is--init');

        if (this.onBoardSubpage) {
            this.checkForSubpage();
        }

        if (activeTabParam !== null) {
            const activeTabByParam = this.$sliderContainer.querySelector(`[data-tab-nav-anchor="${activeTabParam}"]`);

            // remove activeTab active class (set via cms), but only if activeTab via url param exists
            if (this.$activeTab && activeTabByParam) {
                this.$activeTab.classList.remove('is--active');
                this.$activeTab.setAttribute('aria-pressed', 'false');
            }

            // set new activeTab via url param, if it exists
            if (activeTabByParam) {
                this.$activeTab = activeTabByParam;
                this.$activeTab.classList.add('is--active');
                this.$activeTab.setAttribute('aria-pressed', 'true');
                this.checkBeforeSubmit(this.$activeTab.getAttribute('data-tab-nav-url'), this.$activeTab, true);
            }
        } else {
            if (this.$activeTab && !this.$tabMainDocument) {
                this.checkBeforeSubmit(this.$activeTab.getAttribute('data-tab-nav-url'), this.$activeTab, false);
            }
        }

        if (this.$tabNavItems.length > 0 && this.$target) {
            this.initSlider();
            this.setEvents();
        }

        if (this.$tabNav.classList.contains('is--sticky')) {
            this.observeStickyElement(this.$tabNav);
        }
    }

    observeStickyElement ($target, stickyClass = 'has--sticky') {
        const stickyOffset = parseInt(getComputedStyle($target).top, 10) || 0;
        const sentinel = document.createElement('div');
        sentinel.style.position = 'absolute';
        sentinel.style.top = `${$target.offsetTop - stickyOffset}px`;
        sentinel.style.height = '1px';
        sentinel.style.width = '1px';
        sentinel.style.zIndex = '-1';
        $target.parentElement.insertBefore(sentinel, $target);

        const observer = new IntersectionObserver(
            ([entry]) => {
                const isStickyNow = entry.boundingClientRect.top < stickyOffset && !entry.isIntersecting;
                const targetRect = $target.getBoundingClientRect();
                const isAtStickyPosition = Math.round(targetRect.top) === stickyOffset;

                if (isStickyNow && isAtStickyPosition) {
                    $target.classList.add(stickyClass);
                } else {
                    $target.classList.remove(stickyClass);
                }
            },
            {
                root: null,
                threshold: [0]
            }
        );

        observer.observe(sentinel);
    }

    checkForSubpage () {
        this.$activeTab.classList.remove('is--active');
        this.$activeTab = this.$sliderContainer.querySelector('[data-tab-nav-url="' + this.onBoardSubpage + '"]');
        this.$activeTab.classList.add('is--active');
        this.onBoardSubpage = null;
    }

    setEvents () {
        this.$tabNavItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                if (item.getAttribute('aria-pressed') !== 'true') {
                    this.resetPressedState();
                    item.setAttribute('aria-pressed', 'true');
                    this.checkBeforeSubmit(item.getAttribute('data-tab-nav-url'), item);
                }
            });
        });
    }

    resetPressedState () {
        this.$tabNavItems.forEach(item => {
            item.setAttribute('aria-pressed', 'false');
        });
    }

    checkBeforeSubmit (url, item, scroll = true) {
        if (scroll === true) {
            this.scrollToTabNavigation();
        }

        if (this.ajaxActive === false) {
            if (item !== this.$activeTab) {
                const $activeTab = this.$sliderContainer.querySelector('[data-tab-nav="item"].is--active');

                if ($activeTab) {
                    $activeTab.classList.remove('is--active');
                    item.classList.add('is--active');
                    this.$activeTab = item;
                }
            }
            this.ajaxActive = true;
            this.loader = createElementFromHTML(window.OPTIONS.loader);
            this.$targetWrapper.prepend(this.loader);
            this.$targetWrapper.classList.remove('is--visible');

            if (item.dataset.tabNavMain === undefined) {
                this.sendRequest(url);

                if (this.$elementToHide) {
                    this.$elementToHide.classList.add('is--fade-out');

                    this.waitTimeout = setTimeout(() => {
                        this.$elementToHide.classList.add('is--hidden');
                        this.$elementToHide.classList.remove('is--fade-in');
                        this.waitTimeout = null;
                    }, 1000);
                }
            } else {
                if (this.waitTimeout !== null) {
                    clearTimeout(this.waitTimeout);
                    this.waitTimeout = null;
                }
                this.resetToMain();
            }
        } else {
            console.log('Please wait.....Ajax is running');
        }
    }

    resetToMain () {
        if (this.$elementToHide) {
            this.$elementToHide.classList.remove('is--hidden');
            this.$elementToHide.classList.add('is--fade-in');

            setTimeout(() => {
                this.$elementToHide.classList.remove('is--fade-out');
                this.ajaxActive = false;
            }, 200);

            this.$target.style.height = '0';
            this.$target.innerHTML = '';

            if (this.loader) {
                this.loader.remove();
                this.loader = null;
            }

            setTimeout(() => {
                this.$target.style.height = 'auto';
                const $loading = this.$targetWrapper.querySelector('.loading');

                if ($loading) {
                    $loading.remove();
                }
            }, 200);
        }
    }

    sendRequest (url) {
        this.result = new AjaxLoad(url, {
            requestMethod: 'post',
            onSuccess: (resultText) => {
                this.setResult(resultText);
            },
            onError: (error) => {
                console.warn(error);
            }
        });
    }

    setResult (results) {
        let $result = createElementFromHTML(results);

        this.$target.style.height = this.$target.scrollHeight + 'px';

        this.$target.replaceChildren();

        if (this.loader) {
            this.loader.classList.add('is--hidden');
        }

        if (this.tabNavName) {
            const $tabTarget = $result.querySelector('[data-tab-nav-content="' + this.tabNavName + '"');

            if ($tabTarget) {
                $result = $tabTarget;
            }
        }

        if ($result.length > 1) {
            $result.forEach((item) => {
                this.$target.appendChild(item);
            });
        } else {
            this.$target.appendChild($result);
        }

        checkAndLoadFiles(this.$target).then((data) => {
            window.dispatchEvent(new CustomEvent('content.loaded', {
                view: window,
                bubbles: true,
                cancelable: false,
                detail: {
                    $context: this.$target,
                    ajax: true
                }
            }));
        });

        setTimeout(() => {
            this.$target.style.height = $result.scrollHeight + 'px';
        }, 50);

        setTimeout(() => {
            this.$targetWrapper.classList.add('is--visible');
        }, 250);

        setTimeout(() => {
            this.$target.style.height = 'auto';
            this.$target.setAttribute('tabindex', '0');

            setTimeout(() => {
                this.$target.removeAttribute('tabindex', '0');
            }, 400);

            if (this.loader) {
                this.loader.remove();
                this.loader = null;
            }
            this.ajaxActive = false;
        }, 550);

        window.bookmark.initialize(this.$target);

        const dataInview = this.$target.querySelectorAll('[data-inview]:not(.inview)');
        setTimeout(() => {
            dataInview.forEach((dataInView) => {
                window.inViewObserver.observe(dataInView);
            });
        }, 100);
    }

    scrollToTabNavigation () {
        let offset = this.$tabNav.offsetHeight;
        if (this.$header) {
            offset = offset + this.$header.offsetHeight;
        }

        const anchorDimensions = getOffset(this.$targetWrapper);
        const anchorDimensionsTop = anchorDimensions.top;
        smoothScrollTo(0, anchorDimensionsTop - offset, 500);
    }

    initSlider () {
        this.slider = new Swiper(this.$sliderContainer, {
            modules: [A11y, Navigation, Keyboard, Mousewheel],
            slidesPerView: 'auto',
            speed: 500,
            autoHeight: false,
            resistanceRatio: 0,
            threshold: 10,
            slideToClickedSlide: true,
            mousewheel: {
                forceToAxis: true
            },
            a11y: {
                prevSlideMessage: window.translations.slider.prevSlideMessage,
                nextSlideMessage: window.translations.slider.nextSlideMessage,
                firstSlideMessage: window.translations.slider.firstSlideMessage,
                lastSlideMessage: window.translations.slider.lastSlideMessage,
                slideLabelMessage: window.translations.slider.slideLabelMessage
            },
            navigation: {
                nextEl: this.$tabNav.querySelector('.tab-nav__next'),
                prevEl: this.$tabNav.querySelector('.tab-nav__prev')
            }
        });
    }
}

export { TabNavigation };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $tabNavs = $context.querySelectorAll('[data-tab-nav="root"]:not(.is--init)');
        for (let i = 0; i < $tabNavs.length; i++) {
            const $tabNav = new TabNavigation($tabNavs[i]);
            $tabNav.initialize();
        }
    }
});
